import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useOutletContext, useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { DndContext, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { slotDndCollisionDetection } from './utils/slotDndCollisionDetection';
import { getScheduleById } from '../../api/schedule';
import { useFilmContext } from '../../contexts/FilmContext';
import { useBreakdownTabContext } from '../../contexts/BreakdownTabContext';
import { useDataCache } from '../../contexts/BreakdownDataCacheContext';
import { useSlotUpdates, useCalDateUpdates, useScheduleUpdates } from '../../hooks/dataUpdates';
import { useCurrentUser } from '../../contexts/CurrentUserContext';
import './Schedule.css';
import type { ScheduleInfo } from '../../types/schedule.types';
import type { SceneWithUserNames, SceneInfo } from '../../types/scene.types';
// 导入 SVG 图标组件
import ScheduleSearch from './Components/ScheduleSearch/ScheduleSearch';
import DateSelect from '../../components/DateSelect/DateSelect';
import Calendar from './Components/Calendar/Calendar';
import TabPages from './Components/TabPages/TabPages';
import { useScheduleDate } from './Hooks/useScheduleDate';
import { useCalDateSlotsSort } from './Hooks/useCalDateSlotsSort';
import { useSlotInCalDateSorting } from './Hooks/useSlotInCalDateSorting';
import { useScheduleClipboard } from './Hooks/useScheduleClipboard';
import { useOptimisticScheduleData } from './Hooks/useOptimisticScheduleData';
import { useAutoFixSlotVisibilityOnSceneLocationChange } from './Hooks/useAutoFixSlotVisibilityOnSceneLocationChange';
import { useSlotLocationOrganize } from './Hooks/useSlotLocationOrganize';
import { useCalDateActions } from './Hooks/useCalDateActions';
import { useCrossDateDnD } from './Hooks/useCrossDateDnD';
import { restoreClipboardItemsToDate } from './Hooks/restoreClipboardItemsToDate';
import { useSlotLocationVisibilityNormalizer } from './Hooks/useSlotLocationVisibilityNormalizer';
import { useScheduleDnDState } from './Hooks/useScheduleDnDState';
import { useScheduleHeaderPopups } from './Hooks/useScheduleHeaderPopups';
import { useScheduleCurrentDate } from './Hooks/useScheduleCurrentDate';
import { useScheduleSelectors } from './Hooks/useScheduleSelectors';
import { useScheduleLocationMode } from './Hooks/useScheduleLocationMode';
// selectors moved to useScheduleSelectors
import { useScheduleLifecycle } from './Hooks/useScheduleLifecycle';
import { formatDateFromDate, parseDate } from '../../utils/dateUtils';
// drag utils moved to useScheduleSelectors
import ButtonDialog from '../../components/ButtonDialog';
import './Components/Calendar/Calendar.css';
import { ScheduleHeaderBar } from './Components/ScheduleHeaderBar/ScheduleHeaderBar';
import { SlotDragOverlay } from './Components/SlotDragOverlay/SlotDragOverlay';
import { getPermissionsByResource, GranteeType, PermissionRole, ResourceType, UserPermissions } from '../../api/userPermissions';
import { getCurrentUserID } from '../../utils/userUtils';

interface Tab {
  id: string;
  title: string;
  active: boolean;
  fixed?: boolean;
  path?: string;
}

interface LayoutContext {
  tabs: Tab[];
  setTabs: React.Dispatch<React.SetStateAction<Tab[]>>;
}

interface ScheduleProps {
  scheduleID?: string; // 可选，如果提供则使用，否则从 URL params 获取
  preloadedSchedule?: ScheduleInfo | null; // 预加载的拍摄计划数据
  loading?: boolean; // 预加载状态
  error?: string | null; // 预加载错误
}

const Schedule: React.FC<ScheduleProps> = ({ 
  scheduleID: scheduleIDProp,
  preloadedSchedule: preloadedScheduleProp,
  loading: loadingProp,
  error: errorProp
}) => {
  const { t } = useTranslation();
  const { scheduleID: scheduleIDFromParams } = useParams<{ scheduleID: string }>();
  const scheduleID = scheduleIDProp || scheduleIDFromParams;
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { tabs, setTabs } = useOutletContext<LayoutContext>();
  const { filmStartShootingDate, reloadCurrentFilmChildren } = useFilmContext();
  const { activeTab, breakdownId } = useBreakdownTabContext();
  const { getBreakdownCache, subscribeBreakdownCache, getSlotCache, setSlotCache, getScheduleCache, subscribeScheduleCache, setScheduleCache, clearSlotCache, clearCalDateCache, getCalDateCache, setCalDateCache } = useDataCache();

  const scheduleMutationHomeRefreshTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const scheduleHomeBreakdownCardRefresh = useCallback(() => {
    if (scheduleMutationHomeRefreshTimerRef.current !== undefined) {
      clearTimeout(scheduleMutationHomeRefreshTimerRef.current);
    }
    scheduleMutationHomeRefreshTimerRef.current = setTimeout(() => {
      scheduleMutationHomeRefreshTimerRef.current = undefined;
      void reloadCurrentFilmChildren();
    }, 600);
  }, [reloadCurrentFilmChildren]);

  const schedulePageDataMutationCallbacks = useMemo(
    () => ({
      onSuccess: () => scheduleHomeBreakdownCardRefresh(),
    }),
    [scheduleHomeBreakdownCardRefresh],
  );

  const {
    createSlotData,
    updateSlotData,
    deleteSlotData,
    shiftSlotsDateData,
    batchPatchSlotLocationVisibilityData,
  } = useSlotUpdates(schedulePageDataMutationCallbacks);
  const { deleteCalDateData, shiftCalDatesDateData, upsertCalDateData } = useCalDateUpdates(schedulePageDataMutationCallbacks);
  const { patchSchedule } = useScheduleUpdates(schedulePageDataMutationCallbacks);
  const { setServerSyncedField, setLocalOnlyField, localOnlyData, serverSyncedData, stripServerSyncedUserLastUsedScheduleByScheduleId } = useCurrentUser();
  
  const [pageSchedule, setPageSchedule] = useState<ScheduleInfo | null>(preloadedScheduleProp || null);
  const [schedulePermissions, setSchedulePermissions] = useState<UserPermissions[]>([]);
  const [breakdownPermissions, setBreakdownPermissions] = useState<UserPermissions[]>([]);
  const [breakdownOwnerID, setBreakdownOwnerID] = useState<string | null>(null);
  const scheduleDetailRef = useRef<ScheduleInfo | null>(pageSchedule);
  scheduleDetailRef.current = pageSchedule;

  const effectiveBreakdownID =
    pageSchedule?.scheduleBelongBreakdownID ?? location.state?.breakdownID ?? breakdownId;

  useEffect(() => {
    if (!scheduleID) {
      setSchedulePermissions([]);
      return;
    }
    let cancelled = false;
    void getPermissionsByResource(ResourceType.SCHEDULE, scheduleID)
      .then((response) => {
        if (!cancelled) setSchedulePermissions(response.data || []);
      })
      .catch((error) => {
        if (!cancelled) console.error('加载拍摄计划权限失败:', error);
      });
    return () => { cancelled = true; };
  }, [scheduleID]);

  useEffect(() => {
    if (!effectiveBreakdownID) {
      setBreakdownPermissions([]);
      setBreakdownOwnerID(null);
      return;
    }
    let cancelled = false;
    void getPermissionsByResource(ResourceType.BREAKDOWN, effectiveBreakdownID)
      .then((response) => {
        if (!cancelled) setBreakdownPermissions(response.data || []);
      })
      .catch((error) => {
        if (!cancelled) console.error('加载分场权限失败:', error);
      });
    const updateOwner = () => {
      setBreakdownOwnerID(
        getBreakdownCache(effectiveBreakdownID)?.breakdownInfo?.breakdownOwnerID || null,
      );
    };
    updateOwner();
    const unsubscribe = subscribeBreakdownCache(effectiveBreakdownID, updateOwner);
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [effectiveBreakdownID, getBreakdownCache, subscribeBreakdownCache]);

  const currentUserID = getCurrentUserID();
  const ownsSchedule = pageSchedule?.scheduleOwnerID === currentUserID;
  const scheduleRole = schedulePermissions.find(
    (permission) => permission.granteeType === GranteeType.USER && permission.granteeID === currentUserID
  )?.role;
  const canEditSchedule = Boolean(
    ownsSchedule || scheduleRole === PermissionRole.ADMIN || scheduleRole === PermissionRole.EDITOR
  );
  const canCopySchedule = Boolean(canEditSchedule || scheduleRole === PermissionRole.COPY);
  const canManageSchedule = Boolean(ownsSchedule || scheduleRole === PermissionRole.ADMIN);
  const breakdownRole = breakdownPermissions.find(
    (permission) => permission.granteeType === GranteeType.USER && permission.granteeID === currentUserID
  )?.role;
  const canEditBreakdown = Boolean(
    breakdownOwnerID === currentUserID ||
    breakdownRole === PermissionRole.ADMIN ||
    breakdownRole === PermissionRole.EDITOR
  );
  const canCreateSchedule = scheduleID ? canEditSchedule : canEditBreakdown;

  const refreshScheduleCalendarDataRef = useRef<() => Promise<void>>(async () => {});

  const {
    currentScheduleId,
    refreshedSchedule,
    scheduleDetailsActiveTab,
    handleDetailsTabChange,
    handleSelectSchedule,
    handleConfirmDeleteSchedule,
  } = useScheduleLifecycle({
    scheduleID,
    preloadedScheduleProp,
    activeTab,
    breakdownId,
    navigate,
    location,
    searchParams,
    setSearchParams,
    localOnlyData,
    serverSyncedData,
    tabs,
    setTabs,
    scheduleDetailRef,
    getSlotCache,
    getScheduleCache,
    setScheduleCache,
    clearSlotCache,
    clearCalDateCache,
    setServerSyncedField: setServerSyncedField as (key: string, value: string) => void,
    setLocalOnlyField: setLocalOnlyField as (field: 'userLastUsedScheduleTab', value: string) => void,
    stripServerSyncedUserLastUsedScheduleByScheduleId,
    refreshScheduleCalendarDataRef,
  });

  // 检测当前 拍摄计划（Schedule）标签页是否激活（通过 BreakdownTabContext 的 activeTab）
  const isScheduleTabActive = useMemo(() => {
    return activeTab === 'schedule';
  }, [activeTab]);
  
  // 始终从 breakdown cache 读取（不在 Schedule.tsx 内保存一份 breakdown 列表）
  const breakdownIDForCache = useMemo(
    () => effectiveBreakdownID,
    [effectiveBreakdownID],
  );
  const [breakdownCacheTick, setBreakdownCacheTick] = useState(0);
  useEffect(() => {
    if (!breakdownIDForCache) return;
    const unsubscribe = subscribeBreakdownCache(breakdownIDForCache, () => {
      setBreakdownCacheTick((x) => x + 1);
    });
    return unsubscribe;
  }, [breakdownIDForCache, subscribeBreakdownCache]);

  const scenes: SceneWithUserNames[] = useMemo(() => {
    if (!breakdownIDForCache) return [];
    // 订阅回调只负责触发 tick；tick 变化需要让这里重新计算一次
    void breakdownCacheTick;
    return getBreakdownCache(breakdownIDForCache)?.scenes ?? [];
  }, [breakdownIDForCache, breakdownCacheTick, getBreakdownCache]);

  // 使用 useScheduleDate：按 currentScheduleId 拉取 slot/calDate/elementRelations。
  // - scheduleCalendarDataLoading：上述日历行数据的请求态（不是 breakdown 列表里「多套 schedule」的加载）。
  // - 参数用 currentScheduleId 而非 URL 里的 scheduleID，保证切换选中 schedule 时值一致更新。
  const { slots: slotList, calDates, elementRelations, loading: scheduleCalendarDataLoading, refresh: refreshScheduleCalendarData } = useScheduleDate(
    currentScheduleId
  );
  refreshScheduleCalendarDataRef.current = refreshScheduleCalendarData;
  const [loading, setLoading] = useState(loadingProp || false);
  const [error, setError] = useState<string | null>(errorProp || null);
  
  // 同步 loadingProp 和 scheduleCalendarDataLoading 的变化
  useEffect(() => {
    // 如果 loadingProp 或 scheduleCalendarDataLoading 任一为 true，则显示加载中
    // 只有当两者都为 false 时，才隐藏加载状态
    setLoading(loadingProp || scheduleCalendarDataLoading);
  }, [loadingProp, scheduleCalendarDataLoading]);
  
  // 同步 errorProp 的变化
  useEffect(() => {
    if (errorProp !== undefined) {
      setError(errorProp);
    }
  }, [errorProp]);
  const [editableScheduleName, setEditableScheduleName] = useState('未命名');
  const { dndState, dndSetters } = useScheduleDnDState();
  const {
    optimisticCalDates,
    setOptimisticCalDates,
    optimisticSlots,
    setOptimisticSlots,
  } = useOptimisticScheduleData({ slotList, calDates });

  // 当前日期的信息（从 Calendar 传递）
  const [currentDateInfo, setCurrentDateInfo] = useState<{
    shootingDayNumber: number | null;
    totalDays: number | null;
    shift: string | undefined;
    totalPages: number;
    featuredRolesQuantity: number;
    crowdRolesQuantity: number;
    cast: string;
  } | null>(null);

  // sensors 配置
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: canEditSchedule ? 5 : Number.MAX_SAFE_INTEGER,
      },
    })
  );
  
  const {
    currentDate,
    currentDateReady,
    setCurrentDate,
    selectedYear,
    setSelectedYear,
    selectedMonth,
    setSelectedMonth,
    handleDateSelect,
    getDateDisplayText,
  } = useScheduleCurrentDate({
    activeTab,
    scheduleID,
    schedule: pageSchedule,
    slotList,
    scheduleCalendarDataLoading,
    userLastUsedCalendarDate: serverSyncedData.userLastUsedCalendarDate,
    setServerSyncedField: setServerSyncedField as any,
  });
  
  const { dateSelect, scheduleSearch } = useScheduleHeaderPopups();

  // 同步 pageSchedule：优先预加载/breakdown schedule 缓存/刷新切片，再通过 slotCache 反查归属分场；
  // 并订阅所属分场的 schedule（breakdown）缓存更新。
  useEffect(() => {
    if (!currentScheduleId) {
      setPageSchedule(null);
      setEditableScheduleName('');
      return;
    }
    
    const syncEditableScheduleFromCaches = () => {
      // 如果已有预加载数据且匹配当前 scheduleID，直接使用
      if (preloadedScheduleProp && preloadedScheduleProp.scheduleID === currentScheduleId) {
        setPageSchedule(preloadedScheduleProp);
        setEditableScheduleName(preloadedScheduleProp.scheduleName || '未命名');
        return;
      }

      if (breakdownId) {
        const scheduleSlice = getScheduleCache(breakdownId);
        if (
          scheduleSlice?.currentScheduleId === currentScheduleId &&
          scheduleSlice.currentScheduleInfo
        ) {
          setPageSchedule(scheduleSlice.currentScheduleInfo);
          setEditableScheduleName(scheduleSlice.currentScheduleInfo.scheduleName || '未命名');
          return;
        }
      }

      // 优先使用刷新后的数据
      if (refreshedSchedule && refreshedSchedule.scheduleID === currentScheduleId) {
        setPageSchedule(refreshedSchedule);
        setEditableScheduleName(refreshedSchedule.scheduleName || '未命名');
        return;
      }
      
      // 通过 slotCache 反查 scheduleBelongBreakdownID，再读 breakdown 侧的 schedules 列表
      const slotCache = getSlotCache(currentScheduleId);
      const scheduleInfo = slotCache?.scheduleInfo;
      if (scheduleInfo?.scheduleBelongBreakdownID) {
        const scheduleSlice = getScheduleCache(scheduleInfo.scheduleBelongBreakdownID);
        if (scheduleSlice?.schedules) {
          const currentSchedule = scheduleSlice.schedules.find((p) => p.scheduleID === currentScheduleId);
          if (currentSchedule) {
            setPageSchedule(currentSchedule);
            setEditableScheduleName(currentSchedule.scheduleName || '未命名');
            return;
          }
        }
      }
      
      // 若 breakdown 缓存未命中，回落到 slotCache 里的 scheduleInfo（即当前 schedule 快照）
      if (scheduleInfo) {
        setPageSchedule(scheduleInfo);
        setEditableScheduleName(scheduleInfo.scheduleName || '未命名');
      } else {
        // 缓存中没有数据，设置为空
        setPageSchedule(null);
        setEditableScheduleName('');
      }
    };
    
    // 立即更新一次
    syncEditableScheduleFromCaches();
    
    // 订阅 schedule（breakdown）缓存：本分场下多套 schedule / currentSchedule 变动时刷新顶栏标题与 pageSchedule
    const slotCache = getSlotCache(currentScheduleId);
    const scheduleInfo = slotCache?.scheduleInfo;
    const breakdownID = scheduleInfo?.scheduleBelongBreakdownID;
    
    if (breakdownID) {
      const unsubscribe = subscribeScheduleCache(breakdownID, () => {
        const scheduleSlice = getScheduleCache(breakdownID);
        if (
          scheduleSlice?.currentScheduleId === currentScheduleId &&
          scheduleSlice.currentScheduleInfo
        ) {
          setPageSchedule(scheduleSlice.currentScheduleInfo);
          setEditableScheduleName(scheduleSlice.currentScheduleInfo.scheduleName || '未命名');
          return;
        }
        if (scheduleSlice?.schedules) {
          const currentSchedule = scheduleSlice.schedules.find((p) => p.scheduleID === currentScheduleId);
          if (currentSchedule) {
            setPageSchedule(currentSchedule);
            setEditableScheduleName(currentSchedule.scheduleName || '未命名');
          }
        }
      });
      return unsubscribe;
    }
  }, [currentScheduleId, preloadedScheduleProp, refreshedSchedule, getSlotCache, getScheduleCache, subscribeScheduleCache, breakdownId]);


  // 处理名称编辑（参考 Budget 页面的实现）
  const handleNameBlur = async () => {
    if (!scheduleID) return;
    if (pageSchedule && editableScheduleName !== pageSchedule.scheduleName) {
      try {
        // 更新拍摄计划名称（使用统一的更新 hook，自动更新缓存和通知订阅者）
        await patchSchedule(scheduleID!, { scheduleName: editableScheduleName });
        // 更新本地状态（缓存已自动更新）
        if (pageSchedule) {
          setPageSchedule({ ...pageSchedule, scheduleName: editableScheduleName });
        }
      } catch (error) {
        console.error('更新拍摄计划名称失败:', error);
        setEditableScheduleName(pageSchedule?.scheduleName || '未命名');
      }
    }
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!scheduleID) return;
    setEditableScheduleName(e.target.value);
  };

  const handleNameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.currentTarget.blur();
    } else if (e.key === 'Escape') {
      setEditableScheduleName(pageSchedule?.scheduleName || '');
    }
  };

  // 处理打开 ScheduleSearch
  const handleOpenScheduleSearch = scheduleSearch.open;

  // 切换到上一月
  const handlePreviousPeriod = () => {
    if (selectedMonth === 1) {
      setSelectedYear(selectedYear - 1);
      setSelectedMonth(12);
    } else {
      setSelectedMonth(selectedMonth - 1);
    }
  };

  // 切换到下一月
  const handleNextPeriod = () => {
    if (selectedMonth === 12) {
      setSelectedYear(selectedYear + 1);
      setSelectedMonth(1);
    } else {
      setSelectedMonth(selectedMonth + 1);
    }
  };

  // 打开 DateSelect 弹窗
  const handleOpenDateSelect = dateSelect.open;

  // 处理选择月份
  const handleSelectMonth = (year: number, month: number) => {
    setSelectedYear(year);
    setSelectedMonth(month);
    dateSelect.close();
  };

  // Calendar 组件的回调：选择年月
  const handleCalendarSelectYearMonth = (year: number, month: number) => {
    setSelectedYear(year);
    setSelectedMonth(month);
  };

  const deleteDateButtonRef = useRef<HTMLButtonElement>(null);

  /** 与 slot 预览一致：按日期取日历格内 .calendar-day-content 宽度 */
  const measureCalendarDayContentWidthByDateStr = useCallback((dateStr: string): number | null => {
    const [y, m, d] = dateStr.split('-').map(Number);
    const cell = document.querySelector(
      `.calendar-day-cell[data-year="${y}"][data-month="${m}"][data-day="${d}"]`,
    ) as HTMLElement | null;
    const contentEl = cell?.querySelector('.calendar-day-content') as HTMLElement | null;
    const w = (contentEl ?? cell)?.getBoundingClientRect().width;
    return w != null && w > 0 ? w : null;
  }, []);

  // 处理回到该日：滚动日历表定位聚焦当前选中日期
  const handleReturnToCurrentDate = useCallback(() => {
    if (!currentDate) return;
    
    // 查找当前日期的日历单元格
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth() + 1;
    const day = currentDate.getDate();
    
    // 查找日历容器（使用更精确的选择器）
    const calendarContainer = document.querySelector('.calendar-day.-grid') || 
                               document.querySelector('.calendar-days-container');
    if (!calendarContainer) return;
    
    // 查找当前日期的单元格
    const targetCell = calendarContainer.querySelector(
      `.calendar-day-cell[data-year="${year}"][data-month="${month}"][data-day="${day}"]`
    ) as HTMLElement;
    
    if (targetCell) {
      targetCell.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [currentDate]);

  const onDateSelect = handleDateSelect;

  // ========== dnd-kit 拖拽处理函数 ==========

  // 根据 sceneID 从 breakdown cache 读取最新 Scene 信息
  const getSceneInfo = useCallback(
    (sceneID: string | undefined): SceneInfo | undefined => {
      if (!sceneID) return undefined;
      return scenes.find((s) => s.sceneID === sceneID);
    },
    [scenes],
  );

  const { handleGroupByLocation: handleOrganizeByLocation } = useSlotLocationOrganize({
    scheduleID,
    optimisticCalDates,
    optimisticSlots,
    getSceneInfo,
    batchPatchSlotLocationVisibilityData,
    t,
  });

  const { showAllLocations, handleShowAllLocationsChange } = useScheduleLocationMode({
    onOrganizeByLocation: handleOrganizeByLocation,
  });
  const areSlotScenesReady = useMemo(
    () =>
      !optimisticSlots.some(
        (slot) => slot.slotBelongSceneID && !getSceneInfo(slot.slotBelongSceneID),
      ),
    [optimisticSlots, getSceneInfo],
  );

  // 每次 拍摄计划（Schedule）标签页从“未激活→激活”时，执行一次全局按地点整理（你期望的最统一方案）
  const prevScheduleTabActiveRef = useRef<boolean>(false);
  const pendingNormalizeOnActivateRef = useRef<boolean>(false);
  useEffect(() => {
    const wasActive = prevScheduleTabActiveRef.current;
    prevScheduleTabActiveRef.current = isScheduleTabActive;

    if (wasActive || !isScheduleTabActive) return;
    pendingNormalizeOnActivateRef.current = true;
  }, [isScheduleTabActive]);

  useEffect(() => {
    if (!pendingNormalizeOnActivateRef.current) return;
    if (!isScheduleTabActive) return;
    if (!scheduleID) return;
    if (showAllLocations) return;
    if (scheduleCalendarDataLoading) return;
    if (optimisticCalDates.length === 0 && optimisticSlots.length === 0) return;
    if (!areSlotScenesReady) return;

    pendingNormalizeOnActivateRef.current = false;
    void handleOrganizeByLocation();
  }, [
    isScheduleTabActive,
    scheduleID,
    showAllLocations,
    scheduleCalendarDataLoading,
    optimisticCalDates.length,
    optimisticSlots.length,
    areSlotScenesReady,
    handleOrganizeByLocation,
  ]);

  const { enqueueAndFlush: normalizeDatesIfNeeded } = useSlotLocationVisibilityNormalizer({
    scheduleID,
    showAllLocations,
    optimisticSlots,
    optimisticCalDates,
    getSceneInfo,
    batchPatchSlotLocationVisibilityData,
  });

  const {
    formatDateToString,
    calDateDtToLocalStr,
    getDateSlots,
    getDateCalDate,
    getCalendarRowIdsForDateStr,
    checkSlotDuplicateFn,
    extractDateFromOverIdFn,
  } = useScheduleSelectors({
    optimisticSlots,
    optimisticCalDates,
    getSceneInfo,
  });

  const { updateCalDateSlotsSort } = useCalDateSlotsSort({
    scheduleID,
    optimisticSlots,
    optimisticCalDates,
    calDates,
    getDateSlots,
    getDateCalDate,
    refreshScheduleCalendarData,
    onAfterSortUpdated: normalizeDatesIfNeeded,
  });

  const { handleInDateSorting } = useSlotInCalDateSorting({
    scheduleID,
    getDateSlots,
    getDateCalDate,
    getSceneInfo,
    updateCalDateSlotsSort,
    refreshScheduleCalendarData,
    setOptimisticCalDates,
  });

  const {
    groups: clipboardGroups,
    addGroup: clipboardAddGroup,
    removeGroupAt: clipboardRemoveGroupAt,
    removeGroupItemAt: clipboardRemoveGroupItemAt,
    removeGroupItemSlotAt: clipboardRemoveGroupItemSlotAt,
  } = useScheduleClipboard(scheduleID);

  const {
    deleteDateDialogVisible: dateActionsDeleteDialogVisible,
    setDeleteDateDialogVisible: setDateActionsDeleteDialogVisible,
    handleAddNote: handleAddNoteFromHook,
    handleInsertDate: handleInsertDateFromHook,
    handleDeleteDateClick: handleDeleteDateClickFromHook,
    handleDeleteDateConfirm: handleDeleteDateConfirmFromHook,
  } = useCalDateActions({
    scheduleID,
    currentDate,
    setCurrentDate: (d) => setCurrentDate(d),
    calDates,
    slotList,
    formatDateToString,
    calDateDtToLocalStr,
    createSlotData,
    upsertCalDateData,
    shiftSlotsDateData,
    shiftCalDatesDateData,
    deleteCalDateData,
    refreshScheduleCalendarData,
    setServerSyncedField: setServerSyncedField as any,
    t,
  });

  useAutoFixSlotVisibilityOnSceneLocationChange({
    scheduleID,
    scheduleBelongBreakdownID: pageSchedule?.scheduleBelongBreakdownID,
    scenes,
    optimisticSlots,
    getBreakdownCache,
    subscribeBreakdownCache,
    batchPatchSlotLocationVisibilityData,
    getCalendarRowIdsForDateStr,
    getSceneInfo,
  });

  const { onDragStart, onDragOver, onDragEnd } = useCrossDateDnD({
    scheduleID,
    currentDate,
    formatDateToString,
    measureCalendarDayContentWidthByDateStr,
    optimisticSlots,
    setOptimisticSlots,
    optimisticCalDates,
    setOptimisticCalDates,
    getDateCalDate,
    getCalendarRowIdsForDateStr,
    getSceneInfo,
    extractDateFromOverIdFn,
    checkSlotDuplicateFn,
    handleInDateSorting,
    updateCalDateSlotsSort,
    refreshScheduleCalendarData,
    createSlotData,
    updateSlotData,
    deleteSlotData,
    deleteCalDateData,
    upsertCalDateData,
    getSlotCache,
    setSlotCache,
    getCalDateCache,
    setCalDateCache,
    clipboardAddGroup,
    clipboardRemoveGroupAt,
    clipboardRemoveGroupItemAt,
    clipboardRemoveGroupItemSlotAt,
    setCurrentDate,
    setServerSyncedField: setServerSyncedField as any,
    ...dndSetters,
    forceShowAllLocations: showAllLocations,
  });

  const handleClipboardAddGroupToCurrentDate = useCallback(
    async (groupIndex: number) => {
      if (!scheduleID || !currentDate) return;
      const targetDateStr = formatDateToString(currentDate);
      const group = clipboardGroups[groupIndex];
      if (!group?.items?.length) return;
      try {
        await restoreClipboardItemsToDate({
          scheduleID,
          targetDateStr,
          items: group.items,
          sourceCalDateShift: group.sourceCalDateShift,
          getDateCalDate,
          createSlotData,
          updateCalDateSlotsSort,
        });
        clipboardRemoveGroupAt(groupIndex);
        await refreshScheduleCalendarData();
        setCurrentDate(parseDate(targetDateStr));
        setServerSyncedField('userLastUsedCalendarDate', `${scheduleID};${targetDateStr}`);
      } catch (e) {
        console.error('剪贴组添加到当前日失败:', e);
        await refreshScheduleCalendarData();
      }
    },
    [
      scheduleID,
      currentDate,
      clipboardGroups,
      formatDateToString,
      getDateCalDate,
      createSlotData,
      updateCalDateSlotsSort,
      clipboardRemoveGroupAt,
      refreshScheduleCalendarData,
      setCurrentDate,
      setServerSyncedField,
    ],
  );

  if (loading) {
    return (
      <div className="schedule-container">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <span>{t('common.loading')}</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="schedule-container">
        <div className="error-container">
          <div className="error-message">
            <span>❌ {error}</span>
            <button className="btn-retry" onClick={() => window.location.reload()}>{t('common.retry')}</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={slotDndCollisionDetection}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragEnd={onDragEnd}
    >
      <div className="schedule-container">
        <div className="schedule-layout">
          {/* 左侧区域 - 70% */}
          <div className="schedule-left-panel">
            <ScheduleHeaderBar
              scheduleID={scheduleID}
              scheduleName={editableScheduleName}
              addButtonRef={scheduleSearch.btnRef}
              onOpenScheduleSearch={handleOpenScheduleSearch}
              onNameChange={handleNameChange}
              onNameBlur={handleNameBlur}
              onNameKeyDown={handleNameKeyDown}
              currentDate={currentDate}
              t={t}
              onReturnToCurrentDate={handleReturnToCurrentDate}
              onAddNote={handleAddNoteFromHook}
              showAllLocations={showAllLocations}
              onShowAllLocationsChange={handleShowAllLocationsChange}
              onInsertDate={handleInsertDateFromHook}
              deleteDateButtonRef={deleteDateButtonRef}
              onDeleteDateClick={handleDeleteDateClickFromHook}
              onPreviousPeriod={handlePreviousPeriod}
              onNextPeriod={handleNextPeriod}
              dateSelectBtnRef={dateSelect.btnRef}
              onOpenDateSelect={handleOpenDateSelect}
              getDateDisplayText={getDateDisplayText}
              canEdit={canEditSchedule}
            />

            {/* 日历视图 */}
            <Calendar
              canEdit={canEditSchedule}
              scheduleID={scheduleID}
              filmStartShootingDate={filmStartShootingDate}
              scheduleStartDate={pageSchedule?.scheduleStartDate ?? null}
              scheduleEndDate={pageSchedule?.scheduleEndDate ?? null}
              scheduleTotalDays={pageSchedule?.scheduleTotalDays ?? null}
              patchSchedule={patchSchedule}
              selectedYear={selectedYear}
              selectedMonth={selectedMonth}
              currentDate={currentDate}
              currentDateReady={currentDateReady}
              isScheduleTabActive={isScheduleTabActive}
              calendarDataLoading={scheduleCalendarDataLoading}
              onSelectYearMonth={handleCalendarSelectYearMonth}
              onDateSelect={onDateSelect}
              slots={optimisticSlots}
              calDates={optimisticCalDates}
              scenes={scenes}
              elementRelations={elementRelations}
              breakdownID={pageSchedule?.scheduleBelongBreakdownID ?? location.state?.breakdownID ?? breakdownId}
              calendarInsertHighlight={dndState.calendarInsertHighlight}
              activeDragType={dndState.activeDragType}
              wholeDayDragSourceDateStr={
                dndState.activeDragType === 'wholeDay' && dndState.activeDragData?.sourceDateStr
                  ? String(dndState.activeDragData.sourceDateStr)
                  : null
              }
              forceShowAllLocations={showAllLocations}
              normalizeDatesIfNeeded={normalizeDatesIfNeeded}
              updateCalDateSlotsSort={updateCalDateSlotsSort}
              disabledDateCells={dndState.disabledDateCells}
              activeSortingId={dndState.activeSortingId}
              activeSortingType={dndState.activeSortingType}
              activeSortingDate={dndState.activeSortingDate}
              activeSortingDragLevel={dndState.activeSortingDragLevel}
              activeSlotDragBundleIds={dndState.activeSlotDragBundleIds}
              dragOverDateCell={dndState.dragOverDateCell}
              invalidDropTargets={dndState.invalidDropTargets}
              optimisticCalDates={optimisticCalDates}
              onSlotDelete={async (slotID) => {
                // 立即从乐观更新状态中移除被删除的 slot
                const remainingSolts = optimisticSlots.filter(slot => slot.slotID !== slotID);
                setOptimisticSlots(remainingSolts);
                // 删除后刷新数据
                refreshScheduleCalendarData();
                // 不再需要手动调用 checkAndUpdateStartDate，ScheduleDetailsContent 会通过 useEffect 监听 slots 变化自动更新
              }}
              onSlotCreate={() => {
                // 创建后刷新数据
                refreshScheduleCalendarData();
              }}
              onCurrentDateInfoChange={(info) => {
                setCurrentDateInfo(info);
              }}
            />
          </div>

          {/* 右侧区域 - 30% */}
          <div className="schedule-right-panel">
            <TabPages
              breakdownID={pageSchedule?.scheduleBelongBreakdownID ?? location.state?.breakdownID ?? breakdownId}
              elementRelations={elementRelations}
              scheduleID={scheduleID}
              slots={optimisticSlots}
              calDates={optimisticCalDates}
              schedule={pageSchedule}
              currentDate={currentDate}
              currentDateInfo={currentDateInfo}
              onDateSelect={onDateSelect}
              initialActiveTab={scheduleDetailsActiveTab}
              clipboardGroups={clipboardGroups}
              getSceneInfo={getSceneInfo}
              calendarDnDActive={
                !!(
                  dndState.activeDragId &&
                  (dndState.activeDragType === 'slot' || dndState.activeDragType === 'wholeDay')
                )
              }
              onClipboardRemoveGroup={clipboardRemoveGroupAt}
              onClipboardAddGroupToCurrentDate={handleClipboardAddGroupToCurrentDate}
              onDetailsTabChange={handleDetailsTabChange}
              normalizeDatesIfNeeded={normalizeDatesIfNeeded}
              onSlotDelete={(slotID) => {
                // 删除后刷新数据
                refreshScheduleCalendarData();
              }}
              onScheduleRowsRefresh={async () => {
                await refreshScheduleCalendarData();
                if (scheduleID) {
                  const response = await getScheduleById(scheduleID);
                  const updatedSchedule = response.data;
                  setPageSchedule(updatedSchedule);
                  const breakdownID = updatedSchedule?.scheduleBelongBreakdownID;
                  if (breakdownID) {
                    const cache = getScheduleCache(breakdownID);
                    if (cache?.schedules?.length) {
                      const mergedSchedules = cache.schedules.map((p) =>
                        p.scheduleID === scheduleID ? { ...p, ...updatedSchedule } : p
                      );
                      setScheduleCache(breakdownID, { schedules: mergedSchedules });
                    }
                  }
                }
              }}
              onDateShift={(daysOffset) => {
                // 同步顺延日历当前日期
                if (currentDate && scheduleID) {
                  const newDate = new Date(currentDate);
                  newDate.setDate(newDate.getDate() + daysOffset);
                  setCurrentDate(newDate);
                  // 更新用户行为记忆缓存
                  const cacheValue = `${scheduleID};${formatDateFromDate(newDate)}`;
                  setServerSyncedField('userLastUsedCalendarDate', cacheValue);
                }
              }}
              onSelectSchedule={handleSelectSchedule}
              onConfirmDeleteSchedule={handleConfirmDeleteSchedule}
              onOpenScheduleSearch={handleOpenScheduleSearch}
              canEdit={canEditSchedule}
              canCopy={canCopySchedule}
              canManage={canManageSchedule}
            />
          </div>
        </div>

        {/* ScheduleSearch 弹窗 */}
        <ScheduleSearch
          visible={scheduleSearch.visible}
          onClose={scheduleSearch.close}
          breakdownID={effectiveBreakdownID}
          position={scheduleSearch.position}
          onSelect={handleSelectSchedule}
          excludeRef={scheduleSearch.btnRef}
          canCreate={canCreateSchedule}
        />

        {/* DateSelect 弹窗 */}
        <DateSelect
          visible={dateSelect.visible}
          onClose={dateSelect.close}
          position={dateSelect.position}
          selectedYear={selectedYear}
          selectedMonth={selectedMonth}
          onSelectMonth={handleSelectMonth}
          enableDateSelection={false}
        />

        {/* 删除日期确认对话框 */}
        <ButtonDialog
          visible={dateActionsDeleteDialogVisible}
          onClose={() => setDateActionsDeleteDialogVisible(false)}
          onConfirm={handleDeleteDateConfirmFromHook}
          message={t('schedule.deleteDateConfirmMessage')}
          confirmText={t('schedule.delete')}
          cancelText={t('common.cancel')}
          buttonRef={deleteDateButtonRef}
          align="right"
        />
      </div>

      <SlotDragOverlay
        shouldHideDragOverlay={dndState.shouldHideDragOverlay}
        activeDragType={dndState.activeDragType}
        activeDragData={dndState.activeDragData}
        sceneDragOverlayWidth={dndState.sceneDragOverlayWidth}
        wholeDayOverlayWidth={dndState.wholeDayOverlayWidth}
        slotOverlayWidth={dndState.slotOverlayWidth}
        activeSortingType={dndState.activeSortingType}
        activeSortingId={dndState.activeSortingId}
        activeSortingDate={dndState.activeSortingDate}
        activeSlotDragBundleIds={dndState.activeSlotDragBundleIds}
        optimisticSlots={optimisticSlots}
        getSceneInfo={getSceneInfo}
        getCalendarRowIdsForDateStr={getCalendarRowIdsForDateStr}
        forceShowAllLocations={showAllLocations}
      />
    </DndContext>
  );
};

export default Schedule;
